import React, { useState } from 'react';
import moment from 'moment';
import AddIcon from '@mui/icons-material/Add';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { enqueueSnackbar } from 'notistack';
import {
  ExportHubAPI,
  LicenseDrawdown,
  DrawdownSummary,
} from 'api/ExportHubAPI';
import { formatCurrency } from './licensePresets';
import ImportShipmentsModal from './ImportShipmentsModal';

const STATUS_VARIANT: Record<string, string> = {
  Posted: 'success',
  Draft: 'warning',
  Void: 'muted',
};

// Quantities are stored signed (returns are negative), so String() already
// carries the minus. Rows with no quantity at all read as an em dash.
const fmtQty = (n: number | null): string =>
  n === null || n === undefined ? '—' : String(n);

interface Props {
  groupId: string;
  drawdowns: LicenseDrawdown[];
  summary: DrawdownSummary | null;
  loading: boolean;
  onRecord: () => void;
  onChanged: () => void;
}

/**
 * The per-transaction ledger for one license (spec §7).
 *
 * Every row is one decrement or credit. Only Posted rows move the license's
 * consumed value; Drafts are staged and Voids are reversed but retained —
 * export records are audit artifacts, so a posted row is never deleted, only
 * struck through.
 */
const DrawdownLedger: React.FC<Props> = ({
  groupId,
  drawdowns,
  summary,
  loading,
  onRecord,
  onChanged,
}) => {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const remove = async (d: LicenseDrawdown) => {
    const posted = d.status !== 'Draft';
    const label =
      d.reference || moment(d.transaction_date).format('MM/DD/YYYY');
    const question = posted
      ? `Void ${label}? It stays on the ledger for the audit trail but stops drawing down the license.`
      : `Delete the draft ${label}? It was never counted, so nothing changes.`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(question)) return;
    setBusyId(d.id);
    try {
      const res = await ExportHubAPI.deleteDrawdown(d.id, groupId);
      enqueueSnackbar(res.voided ? 'Transaction voided.' : 'Draft deleted.', {
        variant: 'success',
      });
      onChanged();
    } catch (err) {
      enqueueSnackbar('Failed to remove transaction.', { variant: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const post = async (d: LicenseDrawdown) => {
    // A draft posted from here has to clear the same ceiling check the record
    // modal enforces. Without this, staging an over-limit transaction as a
    // draft and posting it inline is a way around the acknowledgement, and the
    // license ends up over-drawn with no reason recorded against it.
    const authValue = summary?.authorized_value ?? null;
    const authQty = summary?.authorized_quantity ?? null;
    const nextValue = (summary?.posted_value ?? 0) + d.value;
    const nextQty = (summary?.posted_quantity ?? 0) + (d.quantity ?? 0);
    const overValue = authValue !== null && nextValue > authValue;
    const overQty = authQty !== null && nextQty > authQty;

    let overdrawReason: string | undefined = undefined;
    if (overValue || overQty) {
      const ceiling =
        overValue && overQty
          ? 'value and quantity'
          : overValue
          ? 'value'
          : 'quantity';
      // eslint-disable-next-line no-alert
      const reason = window.prompt(
        `Posting this draft takes the license past its authorized ${ceiling}. ` +
          `It will still be recorded, and flagged as an over-draw. Why is it ` +
          `going out?`,
      );
      if (reason === null) return;
      if (!reason.trim()) {
        enqueueSnackbar('A reason is required to post an over-draw.', {
          variant: 'warning',
        });
        return;
      }
      overdrawReason = reason.trim();
    }

    setBusyId(d.id);
    try {
      await ExportHubAPI.updateDrawdown(d.id, groupId, {
        status: 'Posted',
        ...(overdrawReason ? { overdraw_reason: overdrawReason } : {}),
      });
      enqueueSnackbar(
        overdrawReason ? 'Draft posted as an over-draw.' : 'Draft posted.',
        { variant: overdrawReason ? 'warning' : 'success' },
      );
      onChanged();
    } catch (err) {
      enqueueSnackbar('Failed to post draft.', { variant: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const postedCount = summary?.posted_count ?? 0;
  const draftCount = summary?.draft_count ?? 0;
  const countLabel = draftCount
    ? `${postedCount} posted · ${draftCount} draft`
    : `${postedCount} posted`;

  return (
    <>
      <div className='eh-lic-linked eh-dd-ledger'>
        <div className='eh-lic-block-title eh-panel-title-flex'>
          <span>
            Drawdown <span className='eh-count-soft'>({countLabel})</span>
          </span>
          <div className='eh-panel-head-actions'>
            {/* Bulk import is built end-to-end (ImportShipmentsModal +
              POST /shipments/import) but stays gated until it has been
              exercised against real data. Drop `disabled` and the pill to
              switch it on — nothing else needs changing. */}
            <button
              className='eh-mini-btn'
              type='button'
              onClick={() => setImportOpen(true)}
              disabled
              title='Bulk shipment import is not available yet'
            >
              <UploadFileOutlinedIcon style={{ fontSize: 15 }} /> Import
              <span className='export-hub-preview-pill'>Coming soon</span>
            </button>
            <button className='eh-mini-btn' type='button' onClick={onRecord}>
              <AddIcon style={{ fontSize: 15 }} /> Record transaction
            </button>
          </div>
        </div>

        {loading && drawdowns.length === 0 ? (
          <div className='eh-table-skeleton'>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className='eh-skel eh-skel-row' />
            ))}
          </div>
        ) : drawdowns.length === 0 ? (
          <div className='eh-detail-note'>
            No transactions recorded yet. Each shipment against this license
            draws down its authorized value at that shipment&apos;s own price.
          </div>
        ) : (
          <div className='eh-dd-table-scroll'>
            <table className='eh-records-table eh-dd-table'>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Reference</th>
                  <th>Product</th>
                  <th>ECCN</th>
                  <th className='eh-dd-num'>Qty</th>
                  <th className='eh-dd-num'>Unit value</th>
                  <th className='eh-dd-num'>Value</th>
                  <th>Destination</th>
                  <th>Status</th>
                  <th aria-label='Actions' />
                </tr>
              </thead>
              <tbody>
                {drawdowns.map((d) => (
                  <tr
                    key={d.id}
                    className={d.status === 'Void' ? 'eh-dd-void' : undefined}
                  >
                    <td>{moment(d.transaction_date).format('MM/DD/YYYY')}</td>
                    <td className='eh-mono'>{d.reference || '—'}</td>
                    <td>{d.product_name || '—'}</td>
                    <td className='eh-mono'>{d.eccn || '—'}</td>
                    <td className='eh-dd-num'>
                      {fmtQty(d.quantity)}
                      {d.quantity !== null && d.unit_of_measure ? (
                        <span className='eh-dd-uom-tag'>
                          {' '}
                          {d.unit_of_measure}
                        </span>
                      ) : null}
                    </td>
                    <td className='eh-dd-num'>
                      {d.unit_value === null
                        ? '—'
                        : formatCurrency(d.unit_value)}
                      {d.value_overridden && (
                        <span
                          className='eh-dd-flag'
                          title='Total was typed, not calculated from quantity × unit value'
                        >
                          <EditOutlinedIcon style={{ fontSize: 12 }} />
                        </span>
                      )}
                    </td>
                    <td className='eh-dd-num eh-dd-value'>
                      {formatCurrency(d.value)}
                    </td>
                    <td>{d.destination_country || '—'}</td>
                    <td>
                      <span
                        className={`eh-pill eh-pill-${
                          STATUS_VARIANT[d.status] || 'muted'
                        }`}
                      >
                        {d.drawdown_type === 'Return' && d.status === 'Posted'
                          ? 'Return'
                          : d.status}
                      </span>
                      {d.overdraw_reason && (
                        <span
                          className='eh-pill eh-pill-danger eh-dd-over-pill'
                          title={d.overdraw_reason}
                        >
                          Over-draw
                        </span>
                      )}
                    </td>
                    <td className='eh-dd-actions'>
                      <div className='eh-dd-actions-inner'>
                        {d.status === 'Draft' && (
                          <button
                            type='button'
                            className='eh-mini-btn'
                            onClick={() => post(d)}
                            disabled={busyId === d.id}
                          >
                            Post
                          </button>
                        )}
                        {d.status !== 'Void' && (
                          <button
                            type='button'
                            className='eh-linked-x'
                            onClick={() => remove(d)}
                            disabled={busyId === d.id}
                            title={
                              d.status === 'Draft'
                                ? 'Delete draft'
                                : 'Void transaction'
                            }
                            aria-label={
                              d.status === 'Draft'
                                ? 'Delete draft'
                                : 'Void transaction'
                            }
                          >
                            {d.status === 'Draft' ? (
                              <DeleteOutlineIcon style={{ fontSize: 15 }} />
                            ) : (
                              <BlockOutlinedIcon style={{ fontSize: 15 }} />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              {summary && (
                <tfoot>
                  <tr>
                    <td colSpan={4}>Posted total</td>
                    <td className='eh-dd-num'>
                      {summary.posted_quantity || 0}
                    </td>
                    <td />
                    <td className='eh-dd-num eh-dd-value'>
                      {formatCurrency(summary.posted_value)}
                    </td>
                    <td colSpan={3} className='eh-dd-foot-note'>
                      {summary.value_remaining === null
                        ? 'No authorized value recorded yet'
                        : `${formatCurrency(
                            summary.value_remaining,
                          )} remaining of ${formatCurrency(
                            summary.authorized_value,
                          )}`}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>

      {/* Import spans licenses, so a successful run refreshes the whole view. */}
      <ImportShipmentsModal
        open={importOpen}
        groupId={groupId}
        onClose={() => setImportOpen(false)}
        onImported={onChanged}
      />
    </>
  );
};

export default DrawdownLedger;
